/* eslint-disable jsx-a11y/anchor-has-content */
import { FC, useEffect, useState } from 'react'
import Head from 'next/head'
import Slider from 'react-slick'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { GenericFormInput, ProducerContactFormInput, ProducerContactSteps } from './types'
import styles from './ProducerContact.module.scss'
import { TITLE_POSTFIX } from 'constants/seo'
import { Comments, Company, Confirmation, Installation } from './steps'
import RegistrationHeader from 'components/registration-header/RegistrationHeader'
import { generatorContactFlow, generatorProgressLabels } from './steps/constants'
import TitleSection from 'components/title-section/TitleSection'
import LookingForHelp from 'components/looking-for-help/LookingForHelp'
import { NewsArticle as NewsArticleType } from 'components/news-articles/types'
import NewsArticles from 'components/news-articles/NewsArticles'
import NewsArticle from 'components/news-articles/NewsArticle'

const ProducerContact: FC = () => {
  // Next router
  const router = useRouter()
  const { locale: language } = router

  // I18n
  const { t } = useTranslation('generator')

  // Local state
  const [formInput, setFormInput] = useState<ProducerContactFormInput>({})
  const [currentStep, setCurrentStep] = useState<ProducerContactSteps>(ProducerContactSteps.COMPANY_DATA)
  const [newsArticles] = useState<NewsArticleType[]>(language === 'nl' ? NewsArticles.nl : NewsArticles.fr)

  /**
   * Updates formInput local state and handles next step
   *
   * @param next
   * @param current
   * @param data
   */
  const handleChangeStep = <T extends GenericFormInput>(next: ProducerContactSteps, current: ProducerContactSteps, data: T) => {
    setFormInput({
      ...formInput,
      [current]: data
    })
    setCurrentStep(next)
  }

  /**
   * Scroll to top after navigating between steps
   */
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentStep])

  /**
   * Returns the correct content view for the current step
   */
  const getContentForCurrentStep = () => {
    switch (currentStep) {
      case ProducerContactSteps.COMPANY_DATA:
        return (
          <Company
            {...{ formInput }}
            handleNext={(input) => handleChangeStep(ProducerContactSteps.INSTALLATIONS, ProducerContactSteps.COMPANY_DATA, input)}
          />
        )

      case ProducerContactSteps.INSTALLATIONS:
        return (
          <Installation
            {...{ formInput }}
            handleNext={(input) => handleChangeStep(ProducerContactSteps.COMMENTS, ProducerContactSteps.INSTALLATIONS, input)}
            handlePrevious={(input) => handleChangeStep(ProducerContactSteps.COMPANY_DATA, ProducerContactSteps.INSTALLATIONS, input)}
          />
        )

      case ProducerContactSteps.COMMENTS:
        return (
          <Comments
            {...{ formInput, setCurrentStep }}
            handlePrevious={(input) => handleChangeStep(ProducerContactSteps.INSTALLATIONS, ProducerContactSteps.COMMENTS, input)}
          />
        )

      case ProducerContactSteps.CONFIRMATION:
        return <Confirmation />

      default:
        return (
          <Company
            formInput={formInput}
            handleNext={(input) => handleChangeStep(ProducerContactSteps.INSTALLATIONS, ProducerContactSteps.COMPANY_DATA, input)}
          />
        )
    }
  }

  return (
    <>
      {/* SEO */}
      <Head>
        <title>
          {t('seoTitle', 'Opwekker infofiche')} {TITLE_POSTFIX}
        </title>
        <meta property="og:title" key="title" content={`${t('seoTitle', 'Opwekker infofiche')} ${TITLE_POSTFIX}`} />
      </Head>

      {/* NAVIGATION */}
      <RegistrationHeader
        currentStep={currentStep}
        flow={generatorContactFlow}
        progressLabels={generatorProgressLabels}
        translationSpace={'generator'}
        disableHeaderButtonStep={ProducerContactSteps.CONFIRMATION}
        handleNextStep={(step: ProducerContactSteps) => setCurrentStep(step)}
      />

      {/* TITLE SECTION */}
      <TitleSection>
        <div>
          <h3>{t('pageTitle', 'Word energie-opwekker bij Bolt')}</h3>
          <p className={classNames(styles.subtitle, 'margin-bottom-none')}>
            {t('pageSubtitle', 'Vul je gegevens in en wij nemen contact met jou op.')}
          </p>
        </div>

        <div className={styles.help}>
          <LookingForHelp isProducersOverview limitedHeight showAvatar />
        </div>
      </TitleSection>

      {/* CONTENT */}
      <section className={classNames('container', styles.registration)}>
        <div className={classNames('is-flex-row', 'is-flex-centered-direction', styles.wrapper)}>
          {/* FORM */}
          <div className={styles.content}>
            <div className={styles.box}>{getContentForCurrentStep()}</div>
          </div>

          {/* SLIDER */}
          <aside className={styles.sidebar}>
            <Slider
              className={styles.slider}
              customPaging={() => <a />}
              infinite
              slidesToShow={1}
              slidesToScroll={1}
              initialSlide={1}
              centerMode
              centerPadding="0px"
              dots
              dotsClass="slick-dots"
              responsive={[
                {
                  breakpoint: 920,
                  settings: { slidesToShow: 2 }
                },
                {
                  breakpoint: 760,
                  settings: { slidesToShow: 1 }
                }
              ]}
            >
              {newsArticles.map((newsArticle) => (
                <NewsArticle key={newsArticle.id} newsArticle={newsArticle} />
              ))}
            </Slider>
          </aside>
        </div>
      </section>
    </>
  )
}

export default ProducerContact
